import { Trophy, X, Circle } from "lucide-react";
import { Button } from "../ui/button";

export function GameTicTacToe({ match, currentUserId, onMove, onLeave }) {
    const state = match.state;
    const me = match.players.find((p) => p.userId === String(currentUserId));
    const opp = match.players.find((p) => p.userId !== String(currentUserId));
    const myMark = state.marks?.[String(currentUserId)];
    const oppMark = state.marks?.[opp.userId];
    const myTurn = state.turn === String(currentUserId) && !state.winner;

    const handleClick = (i) => {
        if (!myTurn) return;
        if (state.board[i] !== null) return;
        onMove({ index: i });
    };

    return (
        <div className="flex flex-col items-center">
            <PlayerStrip
                left={{ name: me.userName, mark: myMark, isMe: true, active: myTurn && !state.winner }}
                right={{ name: opp.userName, mark: oppMark, active: !myTurn && !state.winner }}
            />

            <div className="grid grid-cols-3 gap-2 my-4" style={{ width: 240 }}>
                {state.board.map((v, i) => {
                    const winningCell = state.winningLine && state.winningLine.includes(i);
                    return (
                        <button
                            key={i}
                            onClick={() => handleClick(i)}
                            disabled={!myTurn || v !== null || !!state.winner}
                            className={`aspect-square rounded-lg border-2 flex items-center justify-center transition-all ${
                                winningCell ? "bg-emerald-100 border-emerald-400" :
                                v ? "bg-white border-gray-200" :
                                myTurn ? "bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400 cursor-pointer" :
                                "bg-gray-50 border-gray-200 cursor-not-allowed"
                            }`}
                        >
                            {v === "X" && <X className={`h-12 w-12 ${winningCell ? "text-emerald-600" : "text-blue-600"} stroke-[3]`} />}
                            {v === "O" && <Circle className={`h-10 w-10 ${winningCell ? "text-emerald-600" : "text-red-500"} stroke-[3]`} />}
                        </button>
                    );
                })}
            </div>

            {state.winner ? (
                <EndCard
                    winner={state.winner}
                    currentUserId={currentUserId}
                    draw={state.winner === "draw"}
                    onLeave={onLeave}
                />
            ) : (
                <p className="text-xs text-gray-500">
                    {myTurn ? "Your turn" : `${opp.userName}'s turn...`}
                </p>
            )}
        </div>
    );
}

function PlayerStrip({ left, right }) {
    return (
        <div className="flex items-center justify-between w-full max-w-md gap-3 mb-1">
            <PlayerChip {...left} side="left" />
            <span className="text-xs font-bold text-gray-400">VS</span>
            <PlayerChip {...right} side="right" />
        </div>
    );
}

function PlayerChip({ name, mark, isMe, active, side }) {
    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border flex-1 transition-all ${
            active ? "border-blue-400 bg-blue-50 shadow" : "border-gray-200 bg-white"
        } ${side === "right" ? "flex-row-reverse text-right" : ""}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm ${
                mark === "X" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"
            }`}>{mark}</div>
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{name}{isMe && <span className="text-[10px] text-gray-400 ml-1">(you)</span>}</div>
            </div>
        </div>
    );
}

function EndCard({ winner, currentUserId, draw, onLeave }) {
    const youWon = winner === String(currentUserId);
    return (
        <div className={`mt-3 px-4 py-3 rounded-lg border text-center max-w-md ${
            draw ? "bg-gray-50 border-gray-200" :
            youWon ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
        }`}>
            <Trophy className={`h-6 w-6 mx-auto mb-1 ${draw ? "text-gray-400" : youWon ? "text-emerald-600" : "text-red-500"}`} />
            <div className={`text-sm font-bold ${draw ? "text-gray-700" : youWon ? "text-emerald-800" : "text-red-700"}`}>
                {draw ? "It's a draw!" : youWon ? "You won!" : "You lost"}
            </div>
            <Button size="sm" variant="outline" onClick={onLeave} className="mt-2">Back to lobby</Button>
        </div>
    );
}
